// Contract creation wizard. Either side can initiate. The other party
// is looked up by userId against the real user-profile API.
//
// Three sections:
//   1. Basics (title / amount / other party)
//   2. Milestones — percent-driven; tail row at the bottom (10/15/20)
//   3. Payment checkpoints — pick ≥2 trigger points; amounts are
//      computed automatically from the milestones each checkpoint covers.

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Search, Info, Upload, FileText } from 'lucide-react';
import { useContracts } from './ContractsContext';
import {
  SMALL_PROJECT_THRESHOLD,
  TAIL_PERCENT_OPTIONS,
  MIN_PAYMENT_CHECKPOINTS,
  ACCEPTANCE_PERIOD_DEFAULTS, ACCEPTANCE_PERIOD_COMPANY_BONUS,
} from './types';
import type {
  PaymentEntryTrigger, AccountType, TailPercent,
  CreateContractRequest, ContractBundle, TailPercent as TailPercentType,
} from './types';
import { contractApiService } from '../../services/contractApiService';
import { userService } from '../../services/userService';
import { translateBackendError } from '../../utils/translateBackendError';
import { useToast } from '../../components/UI/Toast';

interface PartyLookup { nickname: string; accountType: AccountType }

// Normalize the API's accountType string to our union. Treat anything
// that isn't an explicit company as individual.
function normAccountType(v?: string): AccountType {
  return v === 'company' ? 'company' : 'individual';
}

const day = 24 * 3600 * 1000;
const fmtRmb = (n: number) => `¥${Math.round(n).toLocaleString('zh-CN')}`;

// =====================================================================
// Form state types
// =====================================================================

interface MilestoneDraft {
  title: string;
  description: string;
  percent: number;        // user-edited
  daysFromNow: number;
  acceptanceCriteria: string;
  assets: string[];
}

// User picks payment checkpoints by directly choosing which milestones
// each payment covers (must be a contiguous block, in order). Coverage
// determines both the amount and the trigger:
//   covers M1+ (block starting at M1) → trigger is "合同签署后"
//   covers Mi+ where i>1                → trigger is "M(i-1) 验收后"
//   covers tail only                    → trigger is "最终验收后"
interface CheckpointDraft {
  coversMilestoneIndices: number[]; // contiguous; empty allowed only for tail-only payment
  coversTail: boolean;
}

// =====================================================================
// Page
// =====================================================================

export interface ContractCreatePrefill {
  to?: string;
  role?: 'client' | 'creator' | null;
  title?: string;
  // Full prefill from an existing contract (e.g. "修改并重新发起" on a
  // rejected one). When present, seeds every step of the wizard.
  cloneFrom?: ContractBundle;
}

interface ContractCreatePageProps {
  // When embedded (e.g. inside a modal from the chat thread) we skip the
  // full-page shell + back button, take prefill from props instead of the
  // URL, and report the created contract via callbacks instead of routing.
  embedded?: boolean;
  prefill?: ContractCreatePrefill;
  onCreated?: (contractId: string) => void;
  onClose?: () => void;
}

const ContractCreatePage: React.FC<ContractCreatePageProps> = ({ embedded, prefill: prefillProp, onCreated, onClose }) => {
  const nav = useNavigate();
  const location = useLocation();
  const { dispatch, viewerId } = useContracts();
  const { showToast } = useToast();

  // A full contract to clone (e.g. "修改并重新发起" on a rejected one).
  // Comes from props (modal) or router state (full-page navigation).
  const cloneFrom: ContractBundle | undefined =
    prefillProp?.cloneFrom || (location.state as any)?.cloneFrom;
  const [submitting, setSubmitting] = useState(false);

  // Prefill source: props when embedded, otherwise query params (when
  // launched as a full page from a chat thread):
  //   ?to=<partnerUserId>&role=<initiator role>&title=<contract title>
  const prefill = useMemo(() => {
    if (prefillProp) {
      return {
        to: prefillProp.to || '',
        role: (prefillProp.role === 'client' || prefillProp.role === 'creator') ? prefillProp.role : null,
        title: prefillProp.title || '',
      };
    }
    const p = new URLSearchParams(window.location.search);
    return {
      to: p.get('to') || '',
      role: (p.get('role') === 'client' || p.get('role') === 'creator') ? (p.get('role') as 'client' | 'creator') : null,
      title: p.get('title') || '',
    };
  }, [prefillProp]);

  const [step, setStep] = useState<1 | 2 | 3>(1);

  // Derive clone seeds. When cloning, the initiator is the current
  // viewer; the other party is the opposite side of the cloned contract.
  const cloneSeed = useMemo(() => {
    if (!cloneFrom) return null;
    const c = cloneFrom.contract;
    const viewerIsClient = c.client.userId === viewerId;
    const other = viewerIsClient ? c.creator : c.client;
    return {
      role: (viewerIsClient ? 'client' : 'creator') as 'client' | 'creator',
      otherUserId: other.userId,
      otherNickname: other.nickname,
      otherAccountType: other.accountType,
      title: c.title,
      description: c.description,
      totalAmount: c.totalAmount,
      requiresInvoice: c.requiresInvoice,
      tailPercent: (c.tailPercent || 20) as TailPercentType,
    };
  }, [cloneFrom, viewerId]);

  // Step 1 — basics
  const [myInfo, setMyInfo] = useState<PartyLookup | null>(null);
  const [myRole, setMyRole] = useState<'client' | 'creator'>(cloneSeed?.role || prefill.role || 'client');
  const [otherUserId, setOtherUserId] = useState(cloneSeed?.otherUserId || prefill.to);
  const [otherInfo, setOtherInfo] = useState<PartyLookup | null>(null);
  const [otherLookupState, setOtherLookupState] = useState<'idle' | 'loading' | 'found' | 'notfound'>('idle');
  const [title, setTitle] = useState(cloneSeed?.title || prefill.title);
  const [description, setDescription] = useState(cloneSeed?.description || '');
  const [totalAmount, setTotalAmount] = useState(cloneSeed?.totalAmount || 5000);
  // Invoice is a contract-level setting, not per-payment. Default on
  // when the buyer is an enterprise account, off otherwise.
  const [requiresInvoice, setRequiresInvoice] = useState(cloneSeed?.requiresInvoice || false);

  // 合同方式: platform generates the doc, or the user uploads their own
  // PDF (platform terms get appended at signing time).
  const [contractMode, setContractMode] = useState<'generate' | 'upload'>('generate');
  const [pdfOssKey, setPdfOssKey] = useState<string | null>(null);
  const [pdfFileName, setPdfFileName] = useState<string>('');
  const [pdfUploading, setPdfUploading] = useState(false);

  const handlePdfFile = async (file: File) => {
    if (file.type !== 'application/pdf') {
      showToast('请上传 PDF 文件', 'error');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      showToast('PDF 文件不能超过 20MB', 'error');
      return;
    }
    setPdfUploading(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const result = await contractApiService.uploadPdf(file.name, base64);
      if (!result.success) {
        showToast(translateBackendError(result.error, 'PDF 上传失败'), 'error');
        return;
      }
      setPdfOssKey(result.ossKey);
      setPdfFileName(result.fileName);
      showToast('PDF 上传成功', 'success');
    } catch {
      showToast('PDF 读取失败', 'error');
    } finally {
      setPdfUploading(false);
    }
  };

  const [pdfPreviewing, setPdfPreviewing] = useState(false);
  const handlePreviewPdf = async () => {
    if (!pdfOssKey) return;
    setPdfPreviewing(true);
    try {
      const result = await contractApiService.previewUploadedPdf(pdfOssKey, pdfFileName);
      if (!result.success) {
        showToast(translateBackendError(result.error, '无法预览合同文件'), 'error');
        return;
      }
      window.open(result.objectUrl, '_blank');
    } finally {
      setPdfPreviewing(false);
    }
  };

  const projectType = totalAmount < SMALL_PROJECT_THRESHOLD ? 'small' : 'large';
  const buyerAccountType: AccountType =
    myRole === 'client' ? (myInfo?.accountType || 'individual') : (otherInfo?.accountType || 'individual');
  const acceptancePeriodDays = ACCEPTANCE_PERIOD_DEFAULTS[projectType] +
    (buyerAccountType === 'company' ? ACCEPTANCE_PERIOD_COMPANY_BONUS : 0);

  // Load the current user's profile so "我" is shown correctly and the
  // built bundle carries the right nickname/accountType.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await userService.getUserProfile();
        const p = (res as any)?.data?.userProfile || (res as any)?.userProfile;
        if (!cancelled && p) {
          setMyInfo({
            nickname: p.displayName || p.username || '我',
            accountType: normAccountType(p.accountType),
          });
        }
      } catch {
        // Leave myInfo null; submit will fall back to a default.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Auto-lookup the other party as the user types (debounced). The
  // request guard (reqIdRef) ensures only the latest in-flight lookup
  // updates state, so fast typing doesn't show a stale result.
  const lookupReqRef = React.useRef(0);
  useEffect(() => {
    const uid = otherUserId.trim();
    if (!uid) { setOtherInfo(null); setOtherLookupState('idle'); return; }
    // Only look up plausible user IDs (alphanumeric, like 'b6cl46k').
    // Skip while the field contains spaces or non-ASCII so we never
    // send unescaped path characters to the API.
    if (!/^[A-Za-z0-9_-]+$/.test(uid)) { setOtherInfo(null); setOtherLookupState('idle'); return; }
    setOtherInfo(null);
    setOtherLookupState('loading');
    const myReq = ++lookupReqRef.current;
    const timer = setTimeout(async () => {
      try {
        const res = await userService.getUserProfileById(uid);
        if (myReq !== lookupReqRef.current) return; // superseded by newer input
        const p = (res as any)?.data?.userProfile || (res as any)?.userProfile;
        if (p && p.userId) {
          setOtherInfo({
            nickname: p.displayName || p.username || uid,
            accountType: normAccountType(p.accountType),
          });
          setOtherLookupState('found');
        } else {
          setOtherLookupState('notfound');
        }
      } catch {
        if (myReq === lookupReqRef.current) setOtherLookupState('notfound');
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [otherUserId]);

  // Step 2 — milestones + tail. Seed from a cloned contract when present.
  const [milestones, setMilestones] = useState<MilestoneDraft[]>(() => {
    if (cloneFrom && cloneFrom.milestones.length > 0) {
      const now = Date.now();
      return cloneFrom.milestones
        .slice()
        .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
        .map(m => ({
          title: m.title,
          description: m.description || '',
          percent: m.percent,
          daysFromNow: m.dueAt ? Math.max(1, Math.round((m.dueAt - now) / (24 * 3600 * 1000))) : 7,
          acceptanceCriteria: m.acceptanceCriteria || '',
          assets: (m.requiredAssets || []).map(a => a.label),
        }));
    }
    return [
      { title: 'M1', description: '', percent: 50, daysFromNow: 7, acceptanceCriteria: '', assets: [] },
      { title: 'M2', description: '', percent: 30, daysFromNow: 14, acceptanceCriteria: '', assets: [] },
    ];
  });
  const [tailPercent, setTailPercent] = useState<TailPercent>(cloneSeed?.tailPercent || 20);
  // Small projects: 'simple' = one-time full payment, no milestones (default);
  // 'milestones' = phased delivery, still paid in full once at signing.
  const [smallMode, setSmallMode] = useState<'simple' | 'milestones'>(
    cloneFrom && cloneFrom.contract.projectType === 'small' && cloneFrom.milestones.length > 1 ? 'milestones' : 'simple',
  );

  const milestonePercentSum = milestones.reduce((s, m) => s + (m.percent || 0), 0);
  const totalPercentSum = milestonePercentSum + tailPercent;
  const balanced = totalPercentSum === 100;
  // Small + milestones: milestones alone must total 100% (no tail).
  const smallMilestonesBalanced = milestonePercentSum === 100;

  // Step 3 — payment checkpoints. Seed from a cloned contract by mapping
  // each payment's covered milestone ids back to their indices.
  const [checkpoints, setCheckpoints] = useState<CheckpointDraft[]>(() => {
    if (cloneFrom && cloneFrom.paymentPlan.length > 0) {
      const sortedMs = cloneFrom.milestones.slice().sort((a, b) => a.sequenceIndex - b.sequenceIndex);
      const idToIndex = new Map(sortedMs.map((m, i) => [m.id, i]));
      return cloneFrom.paymentPlan
        .slice()
        .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
        .map(p => ({
          coversMilestoneIndices: (p.coversMilestoneIds || [])
            .map(id => idToIndex.get(id))
            .filter((x): x is number => x !== undefined)
            .sort((a, b) => a - b),
          coversTail: !!p.coversTail,
        }));
    }
    return [];
  });

  // Compute each checkpoint's amount, derived trigger, and label.
  const computedCheckpoints = useMemo(() => {
    return checkpoints.map((cp, i) => {
      const milestonePct = cp.coversMilestoneIndices.reduce(
        (s, idx) => s + (milestones[idx]?.percent || 0), 0
      );
      const tailPct = cp.coversTail ? tailPercent : 0;
      const pct = milestonePct + tailPct;
      const amount = Math.round(totalAmount * pct / 100);

      // Derive trigger from coverage. The earliest milestone in
      // this checkpoint's range tells us "this payment must arrive
      // before that milestone starts."
      const sortedIdx = [...cp.coversMilestoneIndices].sort((a, b) => a - b);
      const firstCovered = sortedIdx[0];
      const triggerLabel =
        cp.coversTail && cp.coversMilestoneIndices.length === 0
          ? '最终验收后（尾款）'
          : firstCovered === 0
            ? '合同签署后'
            : firstCovered !== undefined
              ? `${milestones[firstCovered - 1]?.title || `M${firstCovered}`} 验收后`
              : '（无覆盖）';

      return { cp, index: i, milestoneIndices: sortedIdx, coversTail: cp.coversTail, pct, amount, triggerLabel };
    });
  }, [checkpoints, milestones, tailPercent, totalAmount]);

  const checkpointTotalPct = computedCheckpoints.reduce((s, c) => s + c.pct, 0);
  const allMoneyCovered = checkpointTotalPct === 100;
  const meetsMinCheckpoints = checkpoints.length >= MIN_PAYMENT_CHECKPOINTS;
  const hasTailCheckpoint = computedCheckpoints.some(c => c.coversTail);

  // Indices that a given checkpoint may pick from: all milestones not
  // yet covered by previous checkpoints, except checkpoint i can also
  // include the milestones it currently covers itself (so unchecking
  // works). Returns a set of allowed indices.
  const allowedMilestonesFor = (cpIndex: number): Set<number> => {
    const usedByOthers = new Set<number>();
    checkpoints.forEach((c, i) => {
      if (i === cpIndex) return;
      c.coversMilestoneIndices.forEach(idx => usedByOthers.add(idx));
    });
    const allowed = new Set<number>();
    milestones.forEach((_, i) => {
      if (!usedByOthers.has(i)) allowed.add(i);
    });
    return allowed;
  };

  // The set of milestones this checkpoint covers must be a contiguous
  // block starting either at 0 or at (last milestone of previous
  // checkpoint + 1). To enforce that visually, we compute the
  // "expected start" — anything before that is locked, anything from
  // there onward up to the next-checkpoint's-first is selectable.
  const validateContiguous = (cpIndex: number, indices: number[]): boolean => {
    if (indices.length === 0) return true;
    const sorted = [...indices].sort((a, b) => a - b);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i] !== sorted[i - 1] + 1) return false;
    }
    return true;
  };

  // -------------------------------------------------------------------
  // Auto-seed step 3 when the user enters it the first time. Only
  // reached for 大项目 — 小项目 submits directly from step 2.
  // -------------------------------------------------------------------
  const enterStep3 = () => {
    if (checkpoints.length === 0) {
      // Default 大项目: pay 1 covers M1 (minimum required), pay 2
      // covers all remaining milestones, pay 3 covers tail.
      const allRemaining = milestones.map((_, i) => i).slice(1);
      setCheckpoints([
        { coversMilestoneIndices: [0], coversTail: false },
        ...(allRemaining.length > 0 ? [{
          coversMilestoneIndices: allRemaining,
          coversTail: false,
        }] : []),
        { coversMilestoneIndices: [], coversTail: true },
      ]);
    }
    setStep(3);
  };

  const updateMilestone = (i: number, patch: Partial<MilestoneDraft>) => {
    setMilestones(ms => ms.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  };

  const addMilestone = () => {
    setMilestones(ms => [...ms, {
      title: `M${ms.length + 1}`, description: '',
      percent: 0,
      daysFromNow: ms.length === 0 ? 7 : ms[ms.length - 1].daysFromNow + 7,
      acceptanceCriteria: '', assets: [],
    }]);
  };

  const removeMilestone = (i: number) => {
    setMilestones(ms => ms.filter((_, idx) => idx !== i));
    // Drop the removed index from any checkpoint coverage; shift
    // higher indices down by 1.
    setCheckpoints(cps => cps.map(cp => ({
      ...cp,
      coversMilestoneIndices: cp.coversMilestoneIndices
        .filter(idx => idx !== i)
        .map(idx => idx > i ? idx - 1 : idx),
    })));
  };

  const handleSubmit = async () => {
    if (!otherInfo) return;
    if (submitting) return;
    const myParty = myInfo ? { userId: viewerId, ...myInfo } : { userId: viewerId, nickname: 'Me', accountType: 'individual' as AccountType };
    const otherParty = { userId: otherUserId.trim(), ...otherInfo };
    const client = myRole === 'client' ? myParty : otherParty;
    const creator = myRole === 'creator' ? myParty : otherParty;

    const now = Date.now();

    // Build the request payload — milestones and payment plan with
    // synthetic ids that mirror the server-side id pattern. The server
    // ignores incoming ids and assigns its own, but coversMilestoneIds
    // needs to reference *something* the server can resolve. We use
    // sequence indices instead via __draft_m{i} tokens; the server
    // remaps these to its real ids when persisting.
    // Small + simple: one 100% milestone. Small + milestones or large:
    // use the user-defined milestones.
    const useDefinedMilestones = projectType === 'large' || smallMode === 'milestones';
    const reqMilestones = !useDefinedMilestones
      ? [{
          title: '交付',
          description: description || '单一交付',
          percent: 100,
          dueAt: now + 7 * day,
          acceptanceCriteria: '满足约定标准',
          requiredAssets: [],
        }]
      : milestones.map((m, i) => ({
          title: m.title || `M${i + 1}`,
          description: m.description,
          percent: m.percent,
          dueAt: now + m.daysFromNow * day,
          acceptanceCriteria: m.acceptanceCriteria,
          requiredAssets: m.assets.map(a => ({ type: 'other' as const, label: a })),
        }));

    // Use draft milestone ids that the server can map by index. The
    // server-side create() rebuilds payment.coversMilestoneIds from
    // the freshly-assigned milestone ids using the same __draft_m
    // index convention.
    const draftMilestoneIds = reqMilestones.map((_, i) => `__draft_m${i}`);

    // Small projects (simple OR milestones) keep one-time full payment at
    // signing — it just covers every milestone instead of a lone one.
    const reqPayments = projectType === 'small'
      ? [{
          sequenceIndex: 0,
          amount: totalAmount,
          coversMilestoneIds: draftMilestoneIds,
          coversTail: false,
          trigger: 'CONTRACT_SIGNED' as PaymentEntryTrigger,
          isFinalTail: false,
        }]
      : computedCheckpoints.map((c, i) => {
          const sortedIdx = c.milestoneIndices;
          const firstCovered = sortedIdx[0];
          let trigger: PaymentEntryTrigger;
          let triggerMilestoneId: string | undefined;
          if (c.coversTail && sortedIdx.length === 0) {
            trigger = 'FINAL_TAIL';
            triggerMilestoneId = draftMilestoneIds[draftMilestoneIds.length - 1];
          } else if (firstCovered === 0) {
            trigger = 'CONTRACT_SIGNED';
          } else {
            trigger = 'MILESTONE_PASSED';
            triggerMilestoneId = draftMilestoneIds[firstCovered - 1];
          }
          const coverIds = sortedIdx.map(idx => draftMilestoneIds[idx]).filter(Boolean);
          return {
            sequenceIndex: i,
            amount: c.amount,
            coversMilestoneIds: coverIds,
            coversTail: c.coversTail,
            trigger,
            triggerMilestoneId,
            isFinalTail: c.coversTail,
          };
        });

    const request: CreateContractRequest = {
      initiatorRole: myRole,
      client, creator,
      title, description,
      projectType,
      totalAmount,
      tailPercent: projectType === 'large' ? tailPercent : 0,
      requiresInvoice,
      customPdfOssKey: contractMode === 'upload' ? (pdfOssKey || undefined) : undefined,
      milestones: reqMilestones,
      paymentPlan: reqPayments,
    };

    setSubmitting(true);
    try {
      const result = await contractApiService.create(request);
      if (!result.success) {
        showToast(translateBackendError(result.error, '合同创建失败，请稍后重试'), 'error');
        setSubmitting(false);
        return;
      }
      // Reducer keeps a write-through cache so detail page renders
      // immediately without a second fetch.
      dispatch({ type: 'CREATE_BUNDLE', bundle: result.bundle });
      if (embedded) {
        onCreated?.(result.bundle.contract.id);
      } else {
        nav(`/contracts/${result.bundle.contract.id}`);
      }
    } catch (err: any) {
      showToast(translateBackendError(err?.message, '合同创建失败，请稍后重试'), 'error');
      setSubmitting(false);
    }
  };

  // =====================================================================
  // Render
  // =====================================================================

  // The form body. Wrapped below in the full-page shell, or returned
  // bare when embedded in a modal. NOTE: do NOT extract this into a
  // nested component defined inside this render — doing so remounts the
  // subtree on every keystroke and steals input focus.
  const body = (
    <>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <h1 className="text-xl font-semibold text-slate-900 mb-1">发起新合同</h1>
        <p className="text-sm text-slate-500 mb-5">客户和创作者均可发起；另一方在收到后可同意、拒绝或反提建议。</p>

        <div className="flex items-center gap-2 text-xs">
          <StepDot active={step === 1} done={step > 1} label="1. 基本信息" />
          <Connector />
          <StepDot active={step === 2} done={step > 2} label={projectType === 'small' ? '2. 交付方式' : '2. 里程碑 + 尾款'} />
          {projectType === 'large' && (
            <>
              <Connector />
              <StepDot active={step === 3} done={false} label="3. 付款节点" />
            </>
          )}
        </div>
      </div>

      {step === 1 && (
        <Card>
          <GuideBox title="第一步：填写基本信息">
            <p>选择你的身份（甲方 = 出钱的需求方，乙方 = 交付作品的创作者），填写对方用户 ID、项目标题、需求描述与总金额。</p>
            <p>金额将决定项目类型：小项目（&lt; ¥{SMALL_PROJECT_THRESHOLD}）流程更简单，大项目支持里程碑与尾款。付款由平台托管，验收通过后才放款给创作者，双方更有保障。</p>
          </GuideBox>
          <RoleToggle myRole={myRole} setMyRole={setMyRole} myInfo={myInfo || undefined} />

          <div className="mt-5">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              对方用户 ID <span className="text-rose-500">*</span>
            </label>
            <div className="relative">
              <input
                value={otherUserId}
                onChange={e => setOtherUserId(e.target.value)}
                placeholder="请输入对方的用户 ID"
                className="w-full px-3 py-2 pr-9 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
              <Search className="w-4 h-4 text-slate-300 absolute right-3 top-1/2 -translate-y-1/2" />
            </div>
            {otherLookupState === 'loading' && (
              <div className="mt-2 text-xs text-slate-400">查找中...</div>
            )}
            {otherLookupState === 'found' && otherInfo && (
              <div className="mt-2 inline-flex items-center gap-2 text-xs">
                <span className="px-2 py-1 bg-slate-100 text-slate-700 rounded">{otherInfo.nickname}</span>
                <span className="text-slate-400">{otherInfo.accountType === 'company' ? '企业账号' : '个人账号'} · 已自动填充昵称</span>
              </div>
            )}
            {otherLookupState === 'notfound' && (
              <div className="mt-2 text-xs text-rose-600">未找到该用户，请确认用户 ID 是否正确。</div>
            )}
          </div>

          {/* 合同方式: generate vs. upload */}
          <div className="mt-5">
            <label className="block text-sm font-medium text-slate-700 mb-2">合同方式</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setContractMode('generate')}
                className={`text-left px-4 py-3 rounded-lg border transition-colors ${
                  contractMode === 'generate'
                    ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <div className="text-sm font-medium text-slate-900">新建合同</div>
                <div className="text-xs text-slate-500 mt-0.5">填写项目内容，由平台生成合同 PDF。</div>
              </button>
              <button
                type="button"
                onClick={() => setContractMode('upload')}
                className={`text-left px-4 py-3 rounded-lg border transition-colors ${
                  contractMode === 'upload'
                    ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100'
                    : 'border-slate-200 hover:bg-slate-50'
                }`}
              >
                <div className="text-sm font-medium text-slate-900">上传合同 PDF</div>
                <div className="text-xs text-slate-500 mt-0.5">已有正式合同文件时使用，平台条款将作为补充附于其后。</div>
              </button>
            </div>

            {contractMode === 'upload' && (
              <div className="mt-3 rounded-lg border border-dashed border-slate-300 p-4">
                {pdfOssKey ? (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-blue-500 shrink-0" />
                      <span className="text-sm text-slate-700 truncate">{pdfFileName}</span>
                      <span className="text-xs text-emerald-600 shrink-0">已上传</span>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        type="button"
                        onClick={handlePreviewPdf}
                        disabled={pdfPreviewing}
                        className="text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50"
                      >
                        {pdfPreviewing ? '加载中...' : '预览'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setPdfOssKey(null); setPdfFileName(''); }}
                        className="text-xs text-slate-500 hover:text-rose-600"
                      >
                        重新上传
                      </button>
                    </div>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center gap-2 cursor-pointer py-2">
                    <Upload className="w-6 h-6 text-slate-400" />
                    <span className="text-sm text-slate-600">{pdfUploading ? '上传中...' : '点击选择 PDF 文件（≤ 20MB）'}</span>
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      disabled={pdfUploading}
                      onChange={e => { const f = e.target.files?.[0]; if (f) void handlePdfFile(f); e.target.value = ''; }}
                    />
                  </label>
                )}
                <p className="text-[11px] text-slate-400 mt-2 leading-relaxed">
                  即便上传自有合同，下一步仍需设置里程碑与付款节点，平台据此进行资金托管与放款。
                </p>
              </div>
            )}
          </div>

          <div className="mt-5">
            <label className="block text-sm font-medium text-slate-700 mb-1">合同标题</label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="例：品牌短片 60s 全流程制作"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">需求描述</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">项目总金额 (¥)</label>
              <input
                type="number"
                value={totalAmount}
                onChange={e => setTotalAmount(Number(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
              <p className={`text-xs mt-1.5 ${projectType === 'large' ? 'text-purple-700' : 'text-sky-700'}`}>
                {projectType === 'large'
                  ? `≥ ¥${SMALL_PROJECT_THRESHOLD} → 大项目（按里程碑 + 尾款管理）`
                  : `< ¥${SMALL_PROJECT_THRESHOLD} → 小项目（全款托管 / 单一交付）`}
              </p>
            </div>
            <div className="text-xs text-slate-500 pt-7 leading-relaxed">
              <span
                className="inline-block align-middle cursor-help"
                title={`验收期计算规则：\n小项目（< ¥${SMALL_PROJECT_THRESHOLD}）基础 ${ACCEPTANCE_PERIOD_DEFAULTS.small} 天，大项目（≥ ¥${SMALL_PROJECT_THRESHOLD}）基础 ${ACCEPTANCE_PERIOD_DEFAULTS.large} 天；企业客户额外 +${ACCEPTANCE_PERIOD_COMPANY_BONUS} 天。\n当前：${projectType === 'large' ? '大' : '小'}项目${buyerAccountType === 'company' ? ' + 企业客户' : ''}。\n\n创作者提交交付后进入验收期；若客户在验收期内未确认也未提出修改，系统将自动视为验收通过并向创作者放款。`}
              >
                <Info className="w-3 h-3 inline-block mr-1 text-slate-400" />
              </span>
              当前合约预计验收期：<span className="font-semibold text-slate-700">{acceptancePeriodDays} 天</span>
            </div>
          </div>

          <div className="mt-5 rounded-lg border border-slate-200 p-3">
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={requiresInvoice}
                onChange={e => setRequiresInvoice(e.target.checked)}
                className="w-4 h-4"
              />
              <span className="text-sm font-medium text-slate-700">需要发票</span>
            </label>
            <p className="text-xs text-slate-500 mt-1.5 ml-6">
              企业客户通常需要为每笔付款开具发票。该设置作用于本合同所有付款节点。
            </p>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={() => setStep(2)}
              disabled={!otherInfo || !title.trim() || totalAmount <= 0 || (contractMode === 'upload' && !pdfOssKey)}
              className="px-4 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 disabled:opacity-40"
            >
              下一步
            </button>
          </div>
        </Card>
      )}

      {step === 2 && projectType === 'large' && (
        <Card>
          <GuideBox title="第二步：拆分里程碑与尾款">
            <p><b>里程碑</b>是把项目分成几个阶段，每个阶段对应一笔金额、一次交付与验收，风险更低、进度更透明。</p>
            <p><b>尾款</b>是保留到最终资产全部移交后才释放的一部分款项，用于保障最终交付质量。里程碑比例 + 尾款比例之和须等于 100%。</p>
          </GuideBox>
          <h2 className="text-base font-semibold text-slate-900 mb-1">里程碑 + 尾款</h2>
          <p className="text-sm text-slate-500 mb-4">
            按比例分配。里程碑比例 + 尾款比例之和必须等于 100%。
          </p>

          <div className="space-y-3 mb-3">
            {milestones.map((m, i) => (
              <MilestoneRow
                key={i}
                index={i}
                milestone={m}
                totalAmount={totalAmount}
                onChange={(patch) => updateMilestone(i, patch)}
                onRemove={milestones.length > 1 ? () => removeMilestone(i) : undefined}
              />
            ))}
          </div>

          <button onClick={addMilestone} className="w-full py-2 border border-dashed border-slate-300 rounded-xl text-sm text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center gap-1.5 mb-4">
            <Plus className="w-4 h-4" /> 添加里程碑
          </button>

          {/* Tail row pinned at the bottom of the milestone list */}
          <TailRow
            tailPercent={tailPercent}
            setTailPercent={setTailPercent}
            totalAmount={totalAmount}
          />

          <div className="mt-5 rounded-lg border p-3 text-sm flex items-center justify-between gap-3"
            style={{ borderColor: balanced ? 'rgb(167 243 208)' : 'rgb(252 165 165)',
                     background: balanced ? 'rgb(236 253 245)' : 'rgb(254 242 242)' }}>
            <div>
              <span className="text-slate-500">里程碑合计 {milestonePercentSum}% + 尾款 {tailPercent}% = </span>
              <span className={`font-semibold ${balanced ? 'text-emerald-700' : 'text-rose-700'}`}>{totalPercentSum}%</span>
            </div>
            <div className={`text-xs ${balanced ? 'text-emerald-700' : 'text-rose-700'}`}>
              {balanced ? '✓ 已分配 100%' : `仍需调整 ${100 - totalPercentSum > 0 ? '+' : ''}${100 - totalPercentSum}%`}
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(1)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">上一步</button>
            <button
              onClick={enterStep3}
              disabled={!balanced}
              className="px-4 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 disabled:opacity-40"
            >
              下一步
            </button>
          </div>
        </Card>
      )}

      {step === 2 && projectType === 'small' && (
        <Card>
          <GuideBox title="第二步：选择交付方式">
            <p>小项目统一为<b>一次性全额付款</b>，款项由平台托管，验收通过后再放款给创作者。</p>
            <p>若项目分几个阶段完成，可选择<b>分里程碑交付</b>：每个阶段单独验收，进度更清晰（付款方式不变，仍一次付全款）。</p>
          </GuideBox>
          <h2 className="text-base font-semibold text-slate-900 mb-1">交付方式</h2>
          <p className="text-sm text-slate-500 mb-4">
            小项目统一为一次性全额付款（平台托管）。你可以选择单次交付，或拆成多个里程碑分阶段交付与验收。
          </p>

          {/* Mode toggle: 一次性 vs 分里程碑 */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <button
              type="button"
              onClick={() => setSmallMode('simple')}
              className={`text-left px-4 py-3 rounded-lg border transition-colors ${
                smallMode === 'simple' ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <div className="text-sm font-medium text-slate-900">一次性交付</div>
              <div className="text-xs text-slate-500 mt-0.5">单次交付、一次性付款，无里程碑。</div>
            </button>
            <button
              type="button"
              onClick={() => setSmallMode('milestones')}
              className={`text-left px-4 py-3 rounded-lg border transition-colors ${
                smallMode === 'milestones' ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' : 'border-slate-200 hover:bg-slate-50'
              }`}
            >
              <div className="text-sm font-medium text-slate-900">分里程碑交付</div>
              <div className="text-xs text-slate-500 mt-0.5">分阶段交付与验收，仍一次性付全款。</div>
            </button>
          </div>

          {smallMode === 'simple' ? (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-700 space-y-1.5">
              <div>金额：¥{totalAmount.toLocaleString('zh-CN')}（一次性全额付款）</div>
              <div>验收期：{acceptancePeriodDays} 天</div>
              <div>修改次数：2 次（默认）</div>
              {requiresInvoice && <div>需要发票：是</div>}
            </div>
          ) : (
            <>
              <p className="text-sm text-slate-500 mb-3">按比例分配里程碑，合计须等于 100%（小项目无尾款）。</p>
              <div className="space-y-3 mb-3">
                {milestones.map((m, i) => (
                  <MilestoneRow
                    key={i}
                    index={i}
                    milestone={m}
                    totalAmount={totalAmount}
                    onChange={(patch) => updateMilestone(i, patch)}
                    onRemove={milestones.length > 1 ? () => removeMilestone(i) : undefined}
                  />
                ))}
              </div>
              <button onClick={addMilestone} className="w-full py-2 border border-dashed border-slate-300 rounded-xl text-sm text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center gap-1.5 mb-4">
                <Plus className="w-4 h-4" /> 添加里程碑
              </button>
              <div className="rounded-lg border p-3 text-sm flex items-center justify-between gap-3"
                style={{ borderColor: smallMilestonesBalanced ? 'rgb(167 243 208)' : 'rgb(252 165 165)',
                         background: smallMilestonesBalanced ? 'rgb(236 253 245)' : 'rgb(254 242 242)' }}>
                <div>
                  <span className="text-slate-500">里程碑合计 = </span>
                  <span className={`font-semibold ${smallMilestonesBalanced ? 'text-emerald-700' : 'text-rose-700'}`}>{milestonePercentSum}%</span>
                </div>
                <div className={`text-xs ${smallMilestonesBalanced ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {smallMilestonesBalanced ? '已分配 100%' : `仍需调整 ${100 - milestonePercentSum > 0 ? '+' : ''}${100 - milestonePercentSum}%`}
                </div>
              </div>
            </>
          )}

          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(1)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">上一步</button>
            <button
              onClick={handleSubmit}
              disabled={submitting || (smallMode === 'milestones' && !smallMilestonesBalanced)}
              className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-40"
            >
              {submitting ? '提交中...' : '发送给对方'}
            </button>
          </div>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <GuideBox title="第三步：设置付款节点">
            <p><b>付款节点</b>是客户分几次把钱打给平台托管。每个节点覆盖一个或多个里程碑，节点金额由所覆盖的里程碑（和/或尾款）自动计算。</p>
            <p>常见做法：签约先付第一笔，覆盖启动阶段；后续里程碑验收后再付下一笔。这样客户按进度付款，创作者按验收收款，双方风险都更小。最少 {MIN_PAYMENT_CHECKPOINTS} 个节点。</p>
          </GuideBox>
          <h2 className="text-base font-semibold text-slate-900 mb-1">付款节点</h2>
          <p className="text-sm text-slate-500 mb-5">
            选择客户分几次向平台付款。每个节点的金额由它覆盖的里程碑（和/或尾款）自动计算。最少 {MIN_PAYMENT_CHECKPOINTS} 个节点。
          </p>

          <div className="space-y-3 mb-4">
            {checkpoints.map((cp, i) => {
              const computed = computedCheckpoints[i];
              const allowed = allowedMilestonesFor(i);
              return (
                <CheckpointRow
                  key={i}
                  index={i}
                  checkpoint={cp}
                  computed={computed}
                  milestones={milestones}
                  allowedMilestoneIndices={allowed}
                  hasTailCheckpoint={hasTailCheckpoint}
                  onToggleMilestone={(idx) => {
                    setCheckpoints(arr => arr.map((x, k) => {
                      if (k !== i) return x;
                      const has = x.coversMilestoneIndices.includes(idx);
                      const next = has
                        ? x.coversMilestoneIndices.filter(n => n !== idx)
                        : [...x.coversMilestoneIndices, idx].sort((a, b) => a - b);
                      // Reject if non-contiguous after the change.
                      if (!validateContiguous(i, next)) return x;
                      return { ...x, coversMilestoneIndices: next };
                    }));
                  }}
                  onToggleTail={(v) => {
                    setCheckpoints(arr => arr.map((x, k) =>
                      k === i ? { ...x, coversTail: v } : x
                    ));
                  }}
                  onRemove={() => setCheckpoints(arr => arr.filter((_, idx) => idx !== i))}
                />
              );
            })}
          </div>

          <button
            onClick={() => setCheckpoints(arr => [...arr, { coversMilestoneIndices: [], coversTail: false }])}
            className="w-full py-2 border border-dashed border-slate-300 rounded-xl text-xs text-slate-500 hover:bg-slate-50 inline-flex items-center justify-center gap-1.5 mb-4"
          >
            <Plus className="w-3 h-3" /> 添加付款节点
          </button>

          <div className="rounded-lg border p-3 text-sm flex items-center justify-between gap-3"
            style={{ borderColor: allMoneyCovered ? 'rgb(167 243 208)' : 'rgb(252 165 165)',
                     background: allMoneyCovered ? 'rgb(236 253 245)' : 'rgb(254 242 242)' }}>
            <div>
              <span className="text-slate-500">付款节点覆盖 </span>
              <span className={`font-semibold ${allMoneyCovered ? 'text-emerald-700' : 'text-rose-700'}`}>
                {checkpointTotalPct}%
              </span>
              <span className="text-slate-400"> （应为 100%）</span>
            </div>
            <div className={`text-xs ${meetsMinCheckpoints ? 'text-emerald-700' : 'text-rose-700'}`}>
              {meetsMinCheckpoints ? `✓ ${computedCheckpoints.length} 个节点` : `至少 ${MIN_PAYMENT_CHECKPOINTS} 个节点`}
            </div>
          </div>

          <div className="mt-6 flex justify-between">
            <button onClick={() => setStep(2)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-50 rounded-lg">上一步</button>
            <button
              onClick={handleSubmit}
              disabled={!allMoneyCovered || !meetsMinCheckpoints || submitting}
              className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 disabled:opacity-40"
            >
              {submitting ? '提交中...' : '发送给对方'}
            </button>
          </div>
        </Card>
      )}
    </>
  );

  if (embedded) return body;

  return (
    <div className="min-h-screen bg-[#FDFDFD] pt-24 pb-20">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <button onClick={() => nav('/contracts')} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft className="w-4 h-4" /> 返回列表
        </button>
        {body}
      </div>
    </div>
  );
};

// =====================================================================
// Sub-components
// =====================================================================

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 p-6">{children}</div>
);

// A soft blue guidance callout shown at the top of each wizard step to
// explain the concepts a first-time user needs (合同/里程碑/付款节点/托管).
const GuideBox: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50/60 p-4">
    <div className="flex items-center gap-1.5 text-[13px] font-medium text-blue-900 mb-1.5">
      <Info className="w-4 h-4" /> {title}
    </div>
    <div className="text-xs text-blue-900/80 leading-relaxed space-y-1">{children}</div>
  </div>
);

const StepDot: React.FC<{ active: boolean; done: boolean; label: string }> = ({ active, done, label }) => (
  <div className={`px-3 py-1.5 rounded-md ${
    active ? 'bg-slate-900 text-white' : done ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
  }`}>{label}</div>
);

const Connector: React.FC = () => <div className="h-px w-8 bg-slate-200" />;

const RoleToggle: React.FC<{
  myRole: 'client' | 'creator';
  setMyRole: (r: 'client' | 'creator') => void;
  myInfo?: { nickname: string; accountType: AccountType };
}> = ({ myRole, setMyRole, myInfo }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">我作为</label>
    <div className="flex gap-2 mb-1">
      <button onClick={() => setMyRole('client')}
        className={`flex-1 px-3 py-2 rounded-lg text-sm border transition-colors ${
          myRole === 'client' ? 'border-blue-300 bg-blue-50 text-blue-800 font-medium' : 'border-slate-200 hover:bg-slate-50'
        }`}>客户（甲方）</button>
      <button onClick={() => setMyRole('creator')}
        className={`flex-1 px-3 py-2 rounded-lg text-sm border transition-colors ${
          myRole === 'creator' ? 'border-emerald-300 bg-emerald-50 text-emerald-800 font-medium' : 'border-slate-200 hover:bg-slate-50'
        }`}>创作者（乙方）</button>
    </div>
    {myInfo && <p className="text-xs text-slate-400 mt-1">当前账号：{myInfo.nickname} · {myInfo.accountType === 'company' ? '企业' : '个人'}</p>}
  </div>
);

// =====================================================================
// Milestone editor row — percent-driven
// =====================================================================

const MilestoneRow: React.FC<{
  index: number;
  milestone: MilestoneDraft;
  totalAmount: number;
  onChange: (patch: Partial<MilestoneDraft>) => void;
  onRemove?: () => void;
}> = ({ index, milestone, totalAmount, onChange, onRemove }) => {
  const computedAmount = Math.round(totalAmount * (milestone.percent || 0) / 100);
  return (
    <div className="border border-slate-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-500">M{index + 1}</span>
          <input
            value={milestone.title}
            onChange={e => onChange({ title: e.target.value })}
            placeholder="阶段名"
            className="px-2 py-1 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>
        {onRemove && (
          <button onClick={onRemove} className="text-slate-400 hover:text-rose-600">
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <textarea
        value={milestone.description}
        rows={2}
        onChange={e => onChange({ description: e.target.value })}
        placeholder="阶段交付物描述..."
        className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-100"
      />
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div>
          <label className="text-[11px] text-slate-500">比例 (%)</label>
          <input
            type="number"
            min={0} max={100}
            value={milestone.percent}
            onChange={e => onChange({ percent: Math.max(0, Math.min(100, Number(e.target.value) || 0)) })}
            className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          <div className="text-[10px] text-slate-400 mt-1">≈ {fmtRmb(computedAmount)}</div>
        </div>
        <div>
          <label className="text-[11px] text-slate-500">距合同生效天数</label>
          <input
            type="number"
            value={milestone.daysFromNow}
            onChange={e => onChange({ daysFromNow: Number(e.target.value) || 0 })}
            className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>
        <div>
          <label className="text-[11px] text-slate-500">修改次数</label>
          <input
            type="number" value={2} disabled
            className="w-full px-2 py-1.5 border border-slate-200 bg-slate-50 rounded text-sm text-slate-500"
          />
        </div>
      </div>
      <input
        value={milestone.acceptanceCriteria}
        onChange={e => onChange({ acceptanceCriteria: e.target.value })}
        placeholder="验收标准（双方约定的可交付成果与有效交付定义）"
        className="w-full px-2 py-1.5 border border-slate-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-100"
      />
    </div>
  );
};

const TailRow: React.FC<{
  tailPercent: TailPercent;
  setTailPercent: (p: TailPercent) => void;
  totalAmount: number;
}> = ({ tailPercent, setTailPercent, totalAmount }) => {
  return (
    <div className="border-2 border-amber-200 bg-amber-50/40 rounded-xl p-4">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <div className="text-sm font-medium text-amber-900">尾款</div>
          <div className="text-[11px] text-amber-700/80">客户保留至最终资产移交后释放。未到账前不视为已托管资金。</div>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold text-amber-900">≈ {fmtRmb(Math.round(totalAmount * tailPercent / 100))}</div>
          <div className="text-[11px] text-amber-700/80">{tailPercent}%</div>
        </div>
      </div>
      <div className="flex gap-2">
        {TAIL_PERCENT_OPTIONS.map(opt => (
          <button
            key={opt}
            type="button"
            onClick={() => setTailPercent(opt)}
            className={`flex-1 px-3 py-2 rounded-lg text-sm border transition-colors ${
              tailPercent === opt
                ? 'border-amber-400 bg-amber-100 text-amber-900 font-medium'
                : 'border-amber-200 bg-white text-amber-800 hover:bg-amber-50'
            }`}
          >
            {opt}%
          </button>
        ))}
      </div>
    </div>
  );
};

// =====================================================================
// Checkpoint editor row
// =====================================================================

const CheckpointRow: React.FC<{
  index: number;
  checkpoint: CheckpointDraft;
  computed?: {
    milestoneIndices: number[];
    coversTail: boolean;
    pct: number;
    amount: number;
    triggerLabel: string;
  };
  milestones: MilestoneDraft[];
  allowedMilestoneIndices: Set<number>;
  hasTailCheckpoint: boolean;
  onToggleMilestone: (idx: number) => void;
  onToggleTail: (v: boolean) => void;
  onRemove: () => void;
}> = ({ index, checkpoint, computed, milestones, allowedMilestoneIndices,
       hasTailCheckpoint, onToggleMilestone, onToggleTail, onRemove }) => {
  const isTailOnly = checkpoint.coversTail && checkpoint.coversMilestoneIndices.length === 0;

  // Build the set of indices that, if toggled in this row's current
  // state, would yield a contiguous block. A milestone is selectable
  // when it's already selected (so unchecking works), or when adding
  // it keeps the selection contiguous.
  const sortedSel = [...checkpoint.coversMilestoneIndices].sort((a, b) => a - b);
  const minSel = sortedSel[0];
  const maxSel = sortedSel[sortedSel.length - 1];
  const isContiguousIfAdded = (idx: number): boolean => {
    if (sortedSel.length === 0) return true;
    return idx === minSel - 1 || idx === maxSel + 1;
  };

  return (
    <div className={`border rounded-xl p-4 ${isTailOnly ? 'border-amber-200 bg-amber-50/40' : 'border-slate-200'}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[11px] text-slate-400 font-medium mb-0.5">付款 #{index + 1}</div>
          <div className="text-sm font-medium text-slate-900">
            {computed?.triggerLabel || '（待定）'}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-slate-500">付款金额（自动）</div>
          <div className="text-base font-bold text-slate-900">
            {computed ? fmtRmb(computed.amount) : '—'}
          </div>
          <div className="text-[10px] text-slate-400">{computed?.pct ?? 0}%</div>
        </div>
        <button
          onClick={onRemove}
          className="text-slate-400 hover:text-rose-600 mt-0.5"
          title="删除该付款节点"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      {/* Coverage selector — the prominent block per user feedback */}
      <div className="rounded-lg bg-blue-50/60 border border-blue-200 p-3">
        <div className="text-[11px] font-semibold text-blue-900 mb-2 tracking-wide">
          覆盖（选择此次付款覆盖哪些阶段）
        </div>
        <div className="flex flex-wrap gap-2">
          {milestones.map((m, mi) => {
            const checked = checkpoint.coversMilestoneIndices.includes(mi);
            const allowedByOthers = allowedMilestoneIndices.has(mi);
            const allowedByContiguity = checked || isContiguousIfAdded(mi);
            const disabled = !allowedByOthers || !allowedByContiguity;
            return (
              <label
                key={mi}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs transition-colors ${
                  checked
                    ? 'border-blue-400 bg-blue-100 text-blue-900 font-medium'
                    : disabled
                      ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 cursor-pointer'
                }`}
                title={
                  !allowedByOthers ? '已被其他付款节点覆盖'
                  : !allowedByContiguity ? '需保持连续选中（只能勾选当前选中区间相邻的里程碑）'
                  : undefined
                }
              >
                <input
                  type="checkbox"
                  className="w-3 h-3"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => onToggleMilestone(mi)}
                />
                <span>{m.title || `M${mi + 1}`}</span>
                <span className="text-[10px] text-slate-500">{m.percent || 0}%</span>
              </label>
            );
          })}

          {/* Tail option — at most one checkpoint may include the tail */}
          <label
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs transition-colors ${
              checkpoint.coversTail
                ? 'border-amber-400 bg-amber-100 text-amber-900 font-medium'
                : hasTailCheckpoint
                  ? 'border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed'
                  : 'border-amber-200 bg-white text-amber-800 hover:bg-amber-50 cursor-pointer'
            }`}
            title={hasTailCheckpoint && !checkpoint.coversTail ? '尾款已被其他付款节点覆盖' : undefined}
          >
            <input
              type="checkbox"
              className="w-3 h-3"
              checked={checkpoint.coversTail}
              disabled={hasTailCheckpoint && !checkpoint.coversTail}
              onChange={e => onToggleTail(e.target.checked)}
            />
            <span>尾款</span>
          </label>
        </div>
        {computed && computed.milestoneIndices.length === 0 && !computed.coversTail && (
          <div className="text-[10px] text-rose-600 mt-2">该付款节点未覆盖任何阶段，请至少选择一项。</div>
        )}
      </div>
    </div>
  );
};

export default ContractCreatePage;
